<script setup lang="ts">
import { computed, unref } from 'vue';
import { t } from '@nova/core';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';
import { useCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';

import DocumentUploadButton from '../DocumentUploadButton.vue';
import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Document field type - single file upload.
 * Requires storage context via CUSTOM_FORMS_STORAGE_CONTEXT_KEY (warehouseId, storageEndpoint).
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  input: [value: string];
  'toggle-confirm-button': [uploading: boolean];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const storageContext = useCustomFormsStorageContext();
const { rules: validationRules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

const modelValueString = computed(() =>
  typeof props.modelValue === 'string' ? props.modelValue : ''
);

/**
 * The field name goes on the file input only when this component renders the field label itself.
 * With displayLabel off the host already shows the name next to the field (Helios's flow form,
 * YardCheckInCustomFields), and repeating it inside the input duplicated it on screen.
 */
const fileInputLabel = computed(() => (normalizedProps.displayLabel ? props.field.label : null));

const uploadCaption = computed(() =>
  t(
    'customForms.messages.maximumInSizeOnlyOneImageTextZipOrSpreadsheetAllowedToUploadMultipleDocsZipFirstThenUpload',
    {
      size: t('customForms.messages.sizeInMegabytes', { size: 50 }),
      imageLimit: t('customForms.messages.oneImage'),
      typeLimitText: t('global.basicTerms.text'),
      typeLimitZip: t('global.basicTerms.zip'),
      typeLimitSpreadsheet: t('global.basicTerms.spreadsheet')
    }
  )
);

function handleUpdate(value: string | string[]) {
  const v = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
  emit('update:model-value', v);
  emit('input', v);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <div class="custom-field-document">
      <document-upload-button
        :warehouse-id="unref(storageContext).warehouseId"
        :storage-endpoint="unref(storageContext).storageEndpoint"
        :model-value="unref(modelValueString)"
        :required="normalizedProps.required"
        :rules="validationRules"
        :read-only="normalizedProps.readOnly"
        :label="fileInputLabel"
        :placeholder="field.placeholder"
        :multiple="false"
        :variant="outlined ? 'simple' : 'default'"
        @update:model-value="handleUpdate"
        @uploading="emit('toggle-confirm-button', true)"
        @uploaded="emit('toggle-confirm-button', false)"
        @error="emit('toggle-confirm-button', false)" />
      <span class="typography-caption">{{ uploadCaption }}</span>
    </div>
  </custom-field-input-base>
</template>

<style scoped lang="scss">
.custom-field-document {
  width: 100%;
}
</style>
